// These lists are already organization-scoped by the API. Never substitute demo data.
export function records(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.value)) return value.value;
  return [];
}

const key = value => String(value ?? "").trim().toLowerCase();
const groupKey = group => key(group.id ?? group.groupId);

const resources = [
  ["devices", "/v1/devices", "Pirdys"],
  ["statuses", "/v1/device-status", "Pirdy status"],
  ["groups", "/v1/subgroups", "Subgroups"],
  ["sites", "/v1/parent-groups", "Groups"],
  ["playlists", "/v1/playlists", "Playlists"],
  ["content", "/v1/media", "Content"],
  ["branding", "/v1/organization/branding", "Startup logo"],
];

const isList = value => Array.isArray(value) || Array.isArray(value?.items) || Array.isArray(value?.value);

function resourceFailure(resource, path, label, error) {
  return {
    resource: path,
    status: error?.status,
    code: error?.code ?? "pirdy_resource_unavailable",
    message: `${label} could not be refreshed. Existing account data has not been reported as deleted.`,
    usingCachedData: false,
  };
}

export async function loadCustomerWorkspace(request, { onProgress } = {}) {
  const me = await request("/v1/me");
  const workspace = { me, resourceErrors: {}, pendingResources: resources.map(([resource]) => resource) };
  for (const [resource] of resources) workspace[resource] = null;
  let rejected = false;
  const snapshot = () => ({ ...workspace, resourceErrors: { ...workspace.resourceErrors }, pendingResources: [...workspace.pendingResources] });
  onProgress?.(snapshot());
  await Promise.all(resources.map(async ([resource, path, label]) => {
      let value;
      try {
        value = await request(path);
        if (resource !== "branding" && value !== null && !isList(value))
          throw { code: "pirdy_invalid_response" };
      }
      catch (error) {
        if (error?.status === 401 || error?.status === 403) { rejected = true; throw error; }
        workspace.resourceErrors[resource] = resourceFailure(resource, path, label, error);
        value = null;
      }
      if (rejected) return;
      workspace[resource] = value;
      workspace.pendingResources = workspace.pendingResources.filter(key => key !== resource);
      onProgress?.(snapshot());
    }));
  return snapshot();
}

export function buildFleet(workspace) {
  const devices = [...new Map(records(workspace?.devices)
    .filter(device => key(device.deviceId)).map(device => [key(device.deviceId), device])).values()];
  const byId = new Map(devices.map(device => [key(device.deviceId), device]));
  const assigned = new Set();
  const groups = records(workspace?.groups).map(group => {
    const listed = Array.isArray(group.members) ? group.members : [];
    const members = [];
    const unresolved = [];
    const seen = new Set();
    for (const member of listed) {
      const id = key(typeof member === "string" ? member : member?.deviceId);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const device = byId.get(id);
      // A stale group member is not proof of current ownership; never create controls for it.
      if (!device) { unresolved.push(id); continue; }
      members.push(device);
      assigned.add(id);
    }
    return { ...group, members, unresolved };
  });
  const nested = new Set();
  const shared = devices.map(device => ({ device, groups: groups.filter(group => group.members.some(member => key(member.deviceId) === key(device.deviceId))) }))
    .filter(entry => entry.groups.length > 1);
  const sharedIds = new Set(shared.map(entry => key(entry.device.deviceId)));
  for (const group of groups) group.sharedMembers = group.members.filter(device => sharedIds.has(key(device.deviceId)));
  const sites = records(workspace?.sites).map(site => {
    const ids = new Set(records(site.groupIds).map(key));
    const children = groups.filter(group => ids.has(groupKey(group)));
    children.forEach(group => nested.add(groupKey(group)));
    return { ...site, groups: children };
  });
  return { sites, groups: groups.filter(group => !nested.has(groupKey(group))),
    allGroups: groups, shared, standalone: devices.filter(device => !assigned.has(key(device.deviceId))) };
}
